/**
 * Sprint VIS-PHOTO-1 — Camera capture component.
 *
 * Lightweight selfie/face capture for the gate. No external deps.
 *
 * How it works:
 *   1. Mount → request `getUserMedia({video: {facingMode}})` and bind
 *      the stream to a <video> element. Live preview shows.
 *   2. "Capture" → draws current frame to an off-DOM <canvas>, exports
 *      to JPEG blob (q=0.85, same as backend), passes to onCapture(File).
 *   3. After capture, preview the still image with "Retake" + "OK".
 *      Closing the component releases the camera (stream tracks stopped).
 *
 * Browser support: works on Chrome / Edge / Safari / Firefox (modern).
 * Falls back to file <input accept="image/*" capture="user"> when
 * getUserMedia is denied or unavailable (e.g. http on iOS Safari).
 *
 * Privacy: the stream stays local. Nothing is uploaded until the parent
 * calls the actual upload endpoint with the returned File.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Space, Upload } from 'antd'
import {
  CameraOutlined, RetweetOutlined, CheckOutlined, CloseOutlined,
  UploadOutlined, SwapOutlined,
} from '@ant-design/icons'

interface Props {
  /** Called with a JPEG File once the user confirms the capture. */
  onCapture: (file: File) => void
  /** Optional: render a thumbnail of an already-captured / uploaded photo. */
  existingUrl?: string | null
  /** Hide on cancel. Parent controls the wrapping drawer/modal lifecycle. */
  onCancel?: () => void
  /** "user" (selfie) or "environment" (back camera). Default user. */
  facing?: 'user' | 'environment'
}

export default function CameraCapture({
  onCapture, existingUrl, onCancel, facing = 'user',
}: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [stillUrl, setStillUrl] = useState<string | null>(null)
  const [stillFile, setStillFile] = useState<File | null>(null)
  const [activeFacing, setActiveFacing] = useState<'user' | 'environment'>(facing)
  const [starting, setStarting] = useState(true)

  // Stop any active stream — call from cleanup + before re-init.
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }, [])

  // Start (or restart on facing change) the camera stream.
  const startCamera = useCallback(async (mode: 'user' | 'environment') => {
    setStarting(true)
    setError(null)
    stopStream()
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera unavailable on this browser. Use Upload instead.')
      setStarting(false)
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }
    } catch (e: unknown) {
      const err = e as { name?: string; message?: string }
      if (err.name === 'NotAllowedError') {
        setError('Camera permission denied. Allow camera access or use Upload.')
      } else if (err.name === 'NotFoundError') {
        setError('No camera found. Use Upload to pick from file.')
      } else {
        setError(err.message || 'Could not start camera.')
      }
    } finally {
      setStarting(false)
    }
  }, [stopStream])

  // Mount: start camera. Unmount: release.
  useEffect(() => {
    void startCamera(activeFacing)
    return () => { stopStream() }
    // intentionally only on facing change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFacing])

  const capture = () => {
    const v = videoRef.current
    const c = canvasRef.current
    if (!v || !c || v.readyState < 2) return
    const w = v.videoWidth
    const h = v.videoHeight
    c.width = w
    c.height = h
    const ctx = c.getContext('2d')
    if (!ctx) return
    // Mirror selfie horizontally so it matches how the user sees themselves.
    if (activeFacing === 'user') {
      ctx.save()
      ctx.translate(w, 0)
      ctx.scale(-1, 1)
      ctx.drawImage(v, 0, 0, w, h)
      ctx.restore()
    } else {
      ctx.drawImage(v, 0, 0, w, h)
    }
    c.toBlob(
      (blob) => {
        if (!blob) return
        const file = new File([blob], `gate-selfie-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        })
        setStillFile(file)
        setStillUrl(URL.createObjectURL(blob))
      },
      'image/jpeg', 0.85,
    )
  }

  const retake = () => {
    if (stillUrl) URL.revokeObjectURL(stillUrl)
    setStillUrl(null)
    setStillFile(null)
  }

  const confirm = () => {
    if (stillFile) onCapture(stillFile)
    if (stillUrl) URL.revokeObjectURL(stillUrl)
    stopStream()
  }

  // Fallback: file picker (also useful on tablets where camera permission
  // is iffy or where the operator wants to upload an ID photo).
  const handleUpload = (file: File) => {
    if (file) onCapture(file)
    stopStream()
    return false  // prevent AntD default upload
  }

  return (
    <div style={{ textAlign: 'center' }}>
      {error && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message={error}
        />
      )}

      {/* Live preview or still */}
      <div style={{
        position: 'relative', background: '#000', borderRadius: 8,
        overflow: 'hidden', minHeight: 280,
      }}>
        {stillUrl ? (
          <img
            src={stillUrl}
            alt="Captured visitor"
            style={{ width: '100%', maxHeight: 360, objectFit: 'contain' }}
          />
        ) : (
          <video
            ref={videoRef}
            playsInline
            muted
            style={{
              width: '100%',
              maxHeight: 360,
              transform: activeFacing === 'user' ? 'scaleX(-1)' : undefined,
            }}
          />
        )}
        {starting && !stillUrl && !error && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff',
          }}>
            Starting camera…
          </div>
        )}
      </div>
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Existing photo thumbnail (when retaking) */}
      {existingUrl && !stillUrl && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
          Existing photo on file. Capture a new one to replace.
        </div>
      )}

      {/* Action bar */}
      <Space style={{ marginTop: 12, justifyContent: 'center', width: '100%' }}>
        {!stillUrl && !error && (
          <>
            <Button
              type="primary"
              icon={<CameraOutlined />}
              onClick={capture}
              disabled={starting}
            >
              Capture
            </Button>
            <Button
              icon={<SwapOutlined />}
              onClick={() => setActiveFacing(
                activeFacing === 'user' ? 'environment' : 'user',
              )}
            >
              Flip
            </Button>
          </>
        )}
        {stillUrl && (
          <>
            <Button icon={<RetweetOutlined />} onClick={retake}>Retake</Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={confirm}
            >
              Use this photo
            </Button>
          </>
        )}
        {/* Fallback upload always available */}
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={(f) => handleUpload(f as File)}
        >
          <Button icon={<UploadOutlined />}>Upload</Button>
        </Upload>
        {onCancel && (
          <Button icon={<CloseOutlined />} onClick={() => {
            stopStream()
            onCancel()
          }}>Cancel</Button>
        )}
      </Space>
    </div>
  )
}
